import React, { FC, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";

import { useChatStore } from "../../../../hooks/useChatStore";
import useNotifier from "../../../../hooks/useNotifier";
import { channelKey } from "../../../../utils/channelKey";

interface ShareChatModalProps {
  onShareSuccess: (chatId: string, isShared: boolean) => void;
}

const ShareChatModal: FC<ShareChatModalProps> = observer(
  ({ onShareSuccess }) => {
    const notifier = useNotifier();
    const chatStore = useChatStore();
    const theme = useTheme();

    const [isOpInProgress, setIsOpInProgress] = useState(false);

    const isOpen = !!chatStore.chatToShare;

    const handleClose = () => {
      chatStore.setChatToShare(null);
      setIsOpInProgress(false);
    };

    const handleConfirm = async () => {
      const chat = chatStore.chatToShare;
      const chatId = channelKey(chat);
      const wasShared = !!chat?.shared;

      if (!chatId) {
        notifier.showError("No chat selected");
        return;
      }

      try {
        setIsOpInProgress(true);

        await chatStore.shareChat();
        
        const nowShared = !wasShared;
        onShareSuccess(chatId, nowShared);
        handleClose();
        notifier.showSuccess(
          `Chat ${nowShared ? "shared" : "unshared"} successfully`,
        );
      } catch (error: any) {
        setIsOpInProgress(false);
        notifier.showError(
          error?.message || `Failed to ${wasShared ? "unshare" : "share"} chat`,
        );
      }
    };

    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontSize: "19px",
          }}
        >
          {chatStore.chatToShare?.shared ? "Unshare Chat" : "Share Chat"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              paddingTop: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.75,
              }}
            >
              Are you sure you want to{" "}
              {chatStore.chatToShare?.shared ? "unshare" : "share"} this chat?
            </Typography>

            <Alert severity="warning" sx={{ mt: 1 }}>
              {chatStore.chatToShare?.shared
                ? "This chat will be unshared from all users. They will no longer be able to access this chat."
                : "Once shared, this chat can be accessed by other users. You can unshare it later at any time."}
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isOpInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isOpInProgress}
            loading={isOpInProgress}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default ShareChatModal;
